import React, { useEffect, useMemo, useRef } from 'react';
import { DateTime } from 'luxon';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';

// B6 (2026-07-19). The booking request/edit PAGES pick dates the
// way the mobile app's CreateBookingSheet/EditBookingSheet do: a horizontal
// strip of one-tap day chips (weekday / day / month), Dubai "today" forward —
// no popup calendar. Time slots render only AFTER a date is chosen (date
// first, then times appear).
//
// 180 days mirrors mobile's DAYS window. Computed per MOUNT, not at module
// level — mobile learned this the hard way: a module-level constant freezes
// "today" at launch, so an app left open overnight offered yesterday as a
// bookable date. Dubai wall-clock on purpose (the backend's slot rules run in
// Asia/Dubai), matching the pages' own lead-time maths.
//
// If the selected date (an existing booking's, or a resumed draft's) falls
// outside the window, it is appended as its own chip so the selection is
// always visible and never silently lost.
export default function BookingDateStrip({ value, onSelect, disabled = false, label = null }) {
    const stripRef = useRef(null);
    const activeRef = useRef(null);

    // The base window is pinned once per mount; only the appended
    // out-of-window chip tracks `value`.
    const baseDays = useMemo(() => {
        const today = DateTime.now().setZone('Asia/Dubai').startOf('day');
        return Array.from({ length: 180 }, (_, i) => today.plus({ days: i }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const days = useMemo(() => {
        if (!value) return baseDays;
        if (baseDays.some((d) => d.toFormat('yyyy-MM-dd') === value)) return baseDays;
        const extra = DateTime.fromISO(value, { zone: 'Asia/Dubai' });
        return extra.isValid ? [...baseDays, extra] : baseDays;
    }, [baseDays, value]);

    // Bring the selected chip into view ONCE (edit page / resumed draft can
    // start weeks in) — never afterwards, so the strip doesn't yank under the
    // user's pointer as they tap along it.
    useEffect(() => {
        const strip = stripRef.current;
        const chip = activeRef.current;
        if (strip && chip) {
            strip.scrollLeft = Math.max(0, chip.offsetLeft - strip.clientWidth / 2 + chip.clientWidth / 2);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Arrow paging (2026-07-19). Each click slides the strip by exactly five chips, measured
    // from the real chip pitch so it stays correct if the chip size changes.
    const pageByDays = (direction) => {
        const strip = stripRef.current;
        if (!strip) return;
        const first = strip.querySelector('.creq-datechip');
        if (!first) return;
        const gap = parseFloat(getComputedStyle(strip).columnGap || getComputedStyle(strip).gap) || 0;
        const pitch = first.getBoundingClientRect().width + gap;
        strip.scrollBy({ left: direction * pitch * 5, behavior: 'smooth' });
    };

    return (
        <div className="creq-datestrip-block">
            {/* Header: the section label on the left, two SMALL GREY paging
                arrows on the right of the SAME row (2026-07-19). Each arrow moves the strip by 5 days. */}
            <div className="creq-datestrip-head">
                {label || <span />}
                <div className="creq-datestrip-arrows">
                    <button
                        type="button"
                        className="creq-datestrip-arrow"
                        aria-label="Earlier dates (5 days)"
                        onClick={() => pageByDays(-1)}
                        disabled={disabled}
                    >
                        <FontAwesomeIcon icon={faChevronLeft} aria-hidden="true" />
                    </button>
                    <button
                        type="button"
                        className="creq-datestrip-arrow"
                        aria-label="Later dates (5 days)"
                        onClick={() => pageByDays(1)}
                        disabled={disabled}
                    >
                        <FontAwesomeIcon icon={faChevronRight} aria-hidden="true" />
                    </button>
                </div>
            </div>
            <div className="creq-datestrip" ref={stripRef} role="radiogroup" aria-label="Date">
                {days.map((d) => {
                    const ds = d.toFormat('yyyy-MM-dd');
                    const active = value === ds;
                    return (
                        <button
                            key={ds}
                            type="button"
                            role="radio"
                            aria-checked={active}
                            ref={active ? activeRef : undefined}
                            className={`creq-datechip${active ? ' is-active' : ''}`}
                            onClick={() => onSelect(ds)}
                            disabled={disabled}
                        >
                            <span className="creq-datechip-dow">{d.toFormat('ccc')}</span>
                            <span className="creq-datechip-day">{d.toFormat('d')}</span>
                            <span className="creq-datechip-mon">{d.toFormat('LLL')}</span>
                        </button>
                    );
                })}
            </div>
        </div>
    );
}
